import { useEffect, useRef, useState } from "react";
import { Box, TextField, FormHelperText, SxProps, Theme } from "@mui/material";

export interface PhoneCodeInputValues {
  code_1: string;
  code_2: string;
  code_3: string;
  code_4: string;
}

interface PhoneCodeInputProps {
  name: string;
  value?: string;
  disabled?: boolean;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (value: React.ChangeEvent<HTMLInputElement>) => void;
  error?: boolean;
  helperText?: string;
  onComplete?: (value: string) => void;
}

const CustomTextField: SxProps<Theme> = (theme) => ({
  "& input": {
    textAlign: "center",
    fontSize: "25px",
    fontWeight: 400,
    width: "40px",
    height: "60px",
  },
  margin: "8px",
  backgroundColor: theme?.palette?.components?.phoneInput?.main || theme?.palette?.primary?.base,
  "& input::-webkit-outer-spin-button, input::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
  },
  "& input[disabled]": {
    cursor: "not-allowed",
  },
});

const REGEX_CODE = /^[0-9]{1}$/;

export const PhoneCodeInput = (props: PhoneCodeInputProps) => {
  const [inputValues, setInputValues] = useState<PhoneCodeInputValues>({
    code_1: "",
    code_2: "",
    code_3: "",
    code_4: "",
  });

  const inputRef_1 = useRef<HTMLInputElement>(null);
  const inputRef_2 = useRef<HTMLInputElement>(null);
  const inputRef_3 = useRef<HTMLInputElement>(null);
  const inputRef_4 = useRef<HTMLInputElement>(null);

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    const pasteData = e.clipboardData.getData("text").trim().slice(0, 4);

    if (pasteData.length === 4) {
      setInputValues({
        code_1: pasteData[0],
        code_2: pasteData[1],
        code_3: pasteData[2],
        code_4: pasteData[3],
      });
      inputRef_4.current?.focus();
    }
  };

  const simulateOnblurEvent = () => {
    const fullCode = [
      inputValues.code_1 || "",
      inputValues.code_2 || "",
      inputValues.code_3 || "",
      inputValues.code_4 || "",
    ].join("");
    const newEvent = {
      target: {
        name: props.name,
        value: fullCode,
      },
    } as React.FocusEvent<HTMLInputElement>;
    props.onBlur?.(newEvent);
  };

  const simulateChangeEvent = () => {
    const fullCode = [
      inputValues.code_1 || "",
      inputValues.code_2 || "",
      inputValues.code_3 || "",
      inputValues.code_4 || "",
    ].join("");
    const newEvent = {
      target: {
        name: props.name,
        value: fullCode,
      },
    } as React.ChangeEvent<HTMLInputElement>;
    props.onChange?.(newEvent);
  };

  // Sincronizar el estado interno con el prop value cuando viene del padre (formik)
  useEffect(() => {
    if (props.value !== undefined) {
      const currentValue = [inputValues.code_1, inputValues.code_2, inputValues.code_3, inputValues.code_4].join("");
      const newValue = (props.value?.toString() || "").trim().slice(0, 4);

      // Solo actualizar si el valor es diferente para evitar loops infinitos
      if (currentValue !== newValue) {
        const valueStr = newValue.padEnd(4, "").slice(0, 4);
        setInputValues({
          code_1: valueStr[0] || "",
          code_2: valueStr[1] || "",
          code_3: valueStr[2] || "",
          code_4: valueStr[3] || "",
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value]);

  useEffect(() => {
    simulateChangeEvent();

    if (inputValues.code_1 && inputValues.code_2 && inputValues.code_3 && inputValues.code_4) {
      const fullCode = inputValues.code_1 + inputValues.code_2 + inputValues.code_3 + inputValues.code_4;
      props.onComplete?.(fullCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValues]);

  return (
    <Box>
      <Box display="flex" flexDirection="row" justifyContent="center">
        <TextField
          type="text"
          name="code_1"
          variant="outlined"
          margin="dense"
          size="small"
          autoComplete="off"
          onChange={(e) => {
            if (REGEX_CODE.test(e.target.value)) {
              setInputValues((prev: PhoneCodeInputValues) => ({ ...prev, code_1: e.target.value }));
              inputRef_2.current?.focus();
              inputRef_2.current?.select();
            }
          }}
          onBlur={simulateOnblurEvent}
          onKeyUp={(e) => {
            if (e.code === "Backspace") {
              setInputValues((prev: PhoneCodeInputValues) => ({ ...prev, code_1: "" }));
            }
          }}
          onPaste={handlePaste}
          error={props.error}
          sx={CustomTextField}
          inputProps={{
            maxLength: 1,
            inputMode: "numeric",
          }}
          inputRef={inputRef_1}
          disabled={props.disabled}
          value={inputValues.code_1}
        />
        <TextField
          type="text"
          name="code_2"
          variant="outlined"
          margin="dense"
          size="small"
          autoComplete="off"
          onChange={(e) => {
            if (REGEX_CODE.test(e.target.value)) {
              setInputValues((prev: PhoneCodeInputValues) => ({ ...prev, code_2: e.target.value }));
              inputRef_3.current?.focus();
              inputRef_3.current?.select();
            }
          }}
          onBlur={simulateOnblurEvent}
          onKeyUp={(e) => {
            if (e.code === "Backspace") {
              if (inputValues.code_2 !== "") {
                setInputValues((prev: PhoneCodeInputValues) => ({ ...prev, code_2: "" }));
              } else {
                inputRef_1.current?.focus();
              }
            }
          }}
          onPaste={handlePaste}
          error={props.error}
          sx={CustomTextField}
          inputProps={{
            maxLength: 1,
            inputMode: "numeric",
          }}
          inputRef={inputRef_2}
          disabled={props.disabled}
          value={inputValues.code_2}
        />
        <TextField
          type="text"
          name="code_3"
          variant="outlined"
          margin="dense"
          size="small"
          autoComplete="off"
          onChange={(e) => {
            if (REGEX_CODE.test(e.target.value)) {
              setInputValues((prev: PhoneCodeInputValues) => ({ ...prev, code_3: e.target.value }));
              inputRef_4.current?.focus();
              inputRef_4.current?.select();
            }
          }}
          onBlur={simulateOnblurEvent}
          onKeyUp={(e) => {
            if (e.code === "Backspace") {
              if (inputValues.code_3 !== "") {
                setInputValues((prev: PhoneCodeInputValues) => ({ ...prev, code_3: "" }));
              } else {
                inputRef_2.current?.focus();
              }
            }
          }}
          onPaste={handlePaste}
          error={props.error}
          sx={CustomTextField}
          inputProps={{
            maxLength: 1,
            inputMode: "numeric",
          }}
          inputRef={inputRef_3}
          disabled={props.disabled}
          value={inputValues.code_3}
        />
        <TextField
          type="text"
          name="code_4"
          variant="outlined"
          margin="dense"
          size="small"
          autoComplete="off"
          onChange={(e) => {
            if (REGEX_CODE.test(e.target.value)) {
              setInputValues((prev: PhoneCodeInputValues) => ({ ...prev, code_4: e.target.value }));
            }
          }}
          onBlur={simulateOnblurEvent}
          onKeyUp={(e) => {
            if (e.code === "Backspace") {
              if (inputValues.code_4 !== "") {
                setInputValues((prev: PhoneCodeInputValues) => ({ ...prev, code_4: "" }));
              } else {
                inputRef_3.current?.focus();
              }
            }
          }}
          onPaste={handlePaste}
          error={props.error}
          sx={CustomTextField}
          inputProps={{
            maxLength: 1,
            inputMode: "numeric",
          }}
          inputRef={inputRef_4}
          disabled={props.disabled}
          value={inputValues.code_4}
        />
      </Box>

      {props.helperText && <FormHelperText>{props.helperText}</FormHelperText>}
    </Box>
  );
};
